import { z } from "zod"
import { AGENT_PRIMARY_ROLE_USE_CASE, MAX_AGENT_ROLES, parseAgentRolesInput, ROLE_SYSTEM_PROMPT_MAX_LENGTH } from "@/lib/ai/roles/types"
import { ONBOARDING_LANGUAGE_OPTIONS, SignupOnboardingLanguagesSchema } from "@/lib/onboarding/languages"

export const AgentTypeSchema = z.enum(["messaging", "email", "voice"])
export type CreationAgentType = z.infer<typeof AgentTypeSchema>
export const PrepareAgentSchema = z.object({
  type: AgentTypeSchema,
  roles: z.array(z.string().trim().min(1)).min(1).max(MAX_AGENT_ROLES).refine(roles => parseAgentRolesInput(roles).ok, "Choose valid, unique roles."),
  name: z.string().trim().min(1).max(100),
  roleLabel: z.string().trim().max(60).nullable(),
})
export const CreateAgentSchema = z.object({
  draftId: z.string().uuid(),
  name: z.string().trim().min(1).max(100),
  prompt: z.string().trim().min(1).max(ROLE_SYSTEM_PROMPT_MAX_LENGTH),
  languages: SignupOnboardingLanguagesSchema,
  documentIds: z.array(z.string().uuid()).max(2000),
  voice: z.object({ voice_id: z.string().min(1), first_message: z.string().trim().min(5).max(2000) }).optional(),
})
export function agentScope(type: CreationAgentType) {
  switch (type) {
    case "messaging": return "message_agent" as const
    case "email": return "email_agent" as const
    case "voice": return "voice_agent" as const
    default: {
      const exhaustiveType: never = type
      throw new Error(`Unsupported agent type: ${exhaustiveType}`)
    }
  }
}
export function agentPromptUseCase(type: CreationAgentType) {
  return AGENT_PRIMARY_ROLE_USE_CASE[agentScope(type)]
}
export function defaultAgentLanguages(locale: string): string[] {
  const code = locale.toLowerCase().split("-")[0]
  const supported = ONBOARDING_LANGUAGE_OPTIONS.some(option => option.value === code)
  return supported && code !== "en" ? ["en", code] : ["en"]
}
export function withAgentLanguages(prompt: string, languages: readonly string[]): string {
  const names = languages.map(code => ONBOARDING_LANGUAGE_OPTIONS.find(option => option.value === code)?.label ?? code)
  return `${prompt}\n\n## Conversation languages\nSupported languages: ${names.join(", ")}. Reply in the customer's language and switch when appropriate. Default to ${names[0]}.`
}
